import { type CSSProperties } from 'react';

import { type Theme } from '@mui/material/styles';

import { ensureArrayIsNonEmpty } from '@craft/lib';
import { extractExhaustiveKeys } from '@craft/lib/primitives';

import { type QuantitativeSize } from '../../types/sizes';
import { type Color } from '../colors';

/**
 * The identifiers for the different interaction states that can occur within an interactable
 * element, such as a button. The values of this object are CSS pseudo-class / interaction
 * names (e.g. 'hover', 'focus'), which are later mapped to full CSS selectors (e.g. '&:hover')
 * by helper utilities.
 */
export const Interaction = Object.freeze({
  Focus: 'focus',
  FocusVisible: 'focus-visible',
  FocusWithin: 'focus-within',
  Hover: 'hover',
} as const);

export const FocusInteractions = [
  Interaction.Focus,
  Interaction.FocusVisible,
  Interaction.FocusWithin,
] as const;
export type FocusInteraction = (typeof FocusInteractions)[number];

export const isFocusInteraction = (value: unknown): value is FocusInteraction =>
  typeof value === 'string' && FocusInteractions.includes(value as FocusInteraction);

export type Interaction = (typeof Interaction)[keyof typeof Interaction];

export const isInteraction = (value: unknown): value is Interaction =>
  typeof value === 'string' && Object.values(Interaction).includes(value as Interaction);

export type Interactions<I extends Interaction = Interaction> = I | I[];

/**
 * The identifiers for the different style adjustments that an interactable element can exhibit when
 * interacted with in a given way.
 */
export const InteractionIndicator = Object.freeze({
  Background: 'background',
  Border: 'border',
} as const);

/**
 * Represents the style adjustment that can be applied to a given interactable element when that
 * interactable element has a given {@link Interaction}.
 */
export type InteractionIndicator = (typeof InteractionIndicator)[keyof typeof InteractionIndicator];

export type InteractionIndicators<I extends InteractionIndicator = InteractionIndicator> = I | I[];

export const isInteractionIndicator = (value: unknown): value is InteractionIndicator =>
  typeof value === 'string' &&
  Object.values(InteractionIndicator).includes(value as InteractionIndicator);

export const DefaultSxProperty = 'default' as const;
export type DefaultCssProperty = typeof DefaultSxProperty;

export type InteractionStyle<
  Default extends DefaultCssProperty | null = DefaultCssProperty | null,
> = {
  readonly backgroundColor: ((t: Theme) => string) | Color | Default;
  readonly borderColor: ((t: Theme) => string) | Color | Default;
  readonly borderWidth: Default | QuantitativeSize<'px'>;
  readonly useOutline: boolean | Default;
};

export const InteractionStyleProperties = ensureArrayIsNonEmpty(
  extractExhaustiveKeys<InteractionStyle>()({
    backgroundColor: true,
    borderColor: true,
    borderWidth: true,
    useOutline: true,
  }),
);

export type InteractionStyleProperty = (typeof InteractionStyleProperties)[number];

type BaseInteractionSetting = {
  readonly indicator?: InteractionIndicators;
  readonly isDisabled?: boolean;
  readonly isEnabled?: boolean;
  readonly overrides?: Partial<InteractionStyle>;
  readonly style?: CSSProperties;
};

/**
 * Describes a single interaction configuration entry, associating one or more
 * {@link Interaction} values with optional indicator, style, selector, and enable/disable flags.
 *
 * Either `interaction` (single) or `interactions` (array) must be provided, but not both. When
 * `indicator` is set, only the style properties associated with that indicator are applied. When
 * `style` is set without an `indicator`, the full style object is applied directly.
 */
export type InteractionSetting = (
  | {
      readonly interaction: Interaction;
      readonly interactions?: never;
    }
  | {
      readonly interaction?: never;
      readonly interactions: Interaction[];
    }
) &
  BaseInteractionSetting;

/**
 * Returns true if the given setting applies to the provided {@link Interaction}. A plain
 * {@link Interaction} string matches only when it equals the target. An
 * {@link InteractionSetting} matches when its `interaction` or `interactions` includes the
 * target, `isDisabled` is not true, and `isEnabled` is not false.
 */
export const isInteractionSettingApplicableForInteraction = (
  setting: Interaction | InteractionSetting,
  interaction: Interaction,
) => {
  if (isInteraction(setting)) {
    return setting === interaction;
  }
  if (setting.interaction) {
    return (
      setting.interaction === interaction &&
      setting.isEnabled !== false &&
      setting.isDisabled !== true
    );
  }
  return (
    setting.interactions.includes(interaction) &&
    setting.isEnabled !== false &&
    setting.isDisabled !== true
  );
};

/**
 * A CSS selector string targeting a specific interaction state on an element. Must begin with
 * `&:`, `&.`, or `&[` to be a valid nested selector in MUI SX / Emotion styles.
 */
export type _InteractableSelector = `.${string}` | `:${string}` | `[${string}]`;

export type InteractableSelector = _InteractableSelector | NestedInteractableSelector;

export type NestedInteractableSelector<I extends _InteractableSelector = _InteractableSelector> =
  `&${I}`;

const nestSelector = (selector: InteractableSelector): NestedInteractableSelector => {
  if (selector.startsWith('&')) {
    return selector as NestedInteractableSelector;
  }
  return `&${selector}` as NestedInteractableSelector;
};

/**
 * Returns the CSS selectors to use when applying styles for the given {@link Interaction}. If
 * `selectors` provides an override for the interaction, that value (or array of values) is
 * returned. Otherwise falls back to a default: hover uses an extended selector that excludes
 * active focus states to prevent conflicts; all other interactions use `&:<interaction>`.
 *
 * @param {Interaction} interaction The interaction to resolve selectors for.
 * @param {InteractableSelectorMap} [selectors] Optional map of per-interaction selector overrides.
 */
export function interactionSelectors(
  interaction: Interaction,
  selectors?: InteractableSelectorMap,
): NestedInteractableSelector[] {
  if (selectors?.[interaction]) {
    const selector = selectors[interaction];
    return Array.isArray(selector) ? selector.map(nestSelector) : [nestSelector(selector)];
  }
  return [nestSelector(`:${interaction}`)];
}

/**
 * Maps each {@link Interaction} to one or more custom {@link InteractableSelector} values that
 * override the defaults when generating interaction styles.
 */
export type InteractableSelectorMap = Partial<
  Record<Interaction, InteractableSelector | InteractableSelector[]>
>;

export type InteractionSettingsArray = (Interaction | InteractionSetting | null | undefined)[];

/**
 * The top-level input type for specifying how an interactable element should look across
 * interaction states. Accepts an array of {@link InteractionSetting} entries (or plain
 * {@link Interaction} strings), or `null`/`undefined` to disable all interaction styles.
 */
export type InteractionSettings = InteractionSettingsArray | null | undefined;

/**
 * Returns true if `value` strictly equals `arr`, or if `arr` is an array that contains `value`.
 * Returns false when `arr` is undefined.
 *
 * @example
 * ```ts
 * hasInteractionOrIndicator([InteractionIndicator.Border], InteractionIndicator.Border); // true
 * hasInteractionOrIndicator(InteractionIndicator.Border, InteractionIndicator.Border);   // true
 * hasInteractionOrIndicator(undefined, InteractionIndicator.Border);                     // false
 * ```
 */
export const hasInteractionOrIndicator = <I extends Interaction | InteractionIndicator>(
  arr: I | I[] | undefined,
  value: I,
): boolean => {
  if (Array.isArray(arr)) {
    return arr.includes(value);
  } else if (typeof arr === 'string') {
    return arr === value;
  }
  return false;
};
